/**
 * The financing block on the customer's page.
 *
 * A lump sum reads as a big number. The same roof at "$130/month" reads as a
 * decision somebody can make, so the presented estimate shows both. What this
 * file holds is the arithmetic and the exact words — no lender, no APR, no
 * term, because none of those are known until the customer applies and quoting
 * one we cannot honour is the fastest way to lose a job at the kitchen table.
 *
 *     monthly = round(total / 1000) * 13     // $13 per $1,000
 *     display = max(monthly, 99)             // never show less than $99
 *
 * The $99 floor is not a rounding nicety: banks will not write a payment below
 * it, so a smaller figure on the page is a payment no lender will actually
 * offer. Whole dollars throughout — this is an illustration, not a quote, and
 * "as low as $130.47/month" would read as a promise of precision we do not
 * have.
 *
 * **Since 2026-09-22 a rep may type the real terms** (ADR 0201). Jonathon:
 * "we also need to be able to enter in the term and rate so it can show
 * customer their payment. for example a 10000 loan at a 11.9% for 120 months
 * would be $142.90 a month." Foundation Finance has approved and publishes its
 * programmes, so the rate and the term are now things we can know rather than
 * things we would be inventing — which is what the paragraph above was
 * guarding against, not precision itself. When both are typed the page prints
 * the amortised payment to the cent and says what produced it; when they are
 * not, every word above still holds and the illustration is what shows. See
 * `financingPayment` — it is the one function that decides which of the two a
 * page is looking at, and it says so in `quoted`.
 */

import { isCommercialJob } from "./autoExpenses.js";
import { formatMoney, type Cents } from "./money.js";

/** Dollars of monthly payment per $1,000 financed. */
export const FINANCING_RATE_PER_THOUSAND = 13;

/** The smallest monthly payment a lender will write, in whole dollars. */
export const FINANCING_MONTHLY_FLOOR = 99;

/**
 * The illustrated monthly payment, in whole dollars.
 *
 * Returns null for a contract of nothing — a $0 estimate has no payment to
 * illustrate, and the floor would otherwise print "$99/month" on an empty
 * quote.
 */
export function financingMonthly(total: Cents): number | null {
  if (!Number.isFinite(total) || total <= 0) return null;
  const thousands = Math.round(total / 100_000);
  return Math.max(
    thousands * FINANCING_RATE_PER_THOUSAND,
    FINANCING_MONTHLY_FLOOR,
  );
}

/**
 * What an upgrade adds to the monthly payment.
 *
 * Same $13 per $1,000, and deliberately **no $99 floor**. The floor exists
 * because no lender writes a payment below it; an upgrade is not a loan, it is
 * the difference between two of them, and "$3,721 of gutters adds $99 a month"
 * would be a worse lie than the arithmetic it was protecting against.
 *
 * Returns null for an upgrade that rounds to nothing — under $500 there is no
 * monthly difference worth printing.
 */
export function financingMonthlyAddition(amount: Cents): number | null {
  if (!Number.isFinite(amount) || amount <= 0) return null;
  const monthly = Math.round(amount / 100_000) * FINANCING_RATE_PER_THOUSAND;
  return monthly > 0 ? monthly : null;
}

/** "approx $52 more a month", or null when there is nothing to say. */
export function financingAdditionLabel(amount: Cents): string | null {
  const monthly = financingMonthlyAddition(amount);
  return monthly == null ? null : `approximately $${monthly} more a month`;
}

/**
 * The block as it appears, in order. One source for the wording so the page,
 * the preview and any later PDF cannot drift apart.
 */
export interface FinancingBlock {
  heading: string;
  /**
   * Whole dollars, as it always was.
   *
   * Kept because a screen that only wants a number still reads this, and a
   * document frozen before 2026-09-22 carries nothing else. When the rep typed
   * real terms this is the quoted payment rounded — read `monthlyFormatted`
   * instead, which keeps the cents that make it checkable.
   */
  monthly: number;
  /** "$142.90" when quoted, "$130" when illustrated. */
  monthlyFormatted: string;
  /** True when a rate and a term produced the figure. See `financingPayment`. */
  quoted: boolean;
  lines: string[];
  disclaimers: string[];
}

/**
 * The heading when `company_settings.financing_heading` is empty.
 *
 * A fallback, not the source. ADR 0033's whole decision is that a rebrand is a
 * row edit rather than a deploy — and this string is the one piece of
 * customer-facing branding that was hard-coded, so it would have been the one
 * thing a fourth rename could not fix without shipping code.
 */
export const FINANCING_HEADING = "CopperRidge Consumer Credit Center";

export function financingBlock(
  total: Cents,
  heading?: string | null,
  terms?: FinancingTerms | null,
): FinancingBlock | null {
  const payment = financingPayment(total, terms);
  if (payment == null) return null;

  /*
   * "As low as" is what you say about a figure you made up out of a rule of
   * thumb. Once a rate and a term are on the estimate the payment is the
   * payment, and the two lines under it say what produced it — the amount
   * financed and the terms — because a customer who can check the arithmetic
   * is a customer who believes it.
   */
  const lines = payment.quoted
    ? [
        `Payments of ${payment.formatted}/month`,
        ...(payment.downPayment > 0
          ? [
              `${formatMoney(payment.downPayment)} down, ${formatMoney(
                payment.financed,
              )} financed`,
            ]
          : []),
        `${formatTerm(payment.termMonths!)} at ${formatApr(payment.apr!)} APR`,
        "30 days until your first payment",
        "Pay off early with no penalty fee",
      ]
    : [
        `Payments as low as ${payment.formatted}/month`,
        ...(payment.downPayment > 0
          ? [
              `${formatMoney(payment.downPayment)} down, ${formatMoney(
                payment.financed,
              )} financed`,
            ]
          : []),
        "30 days until your first payment",
        "Pay off early with no penalty fee",
      ];

  return {
    heading: heading?.trim() || FINANCING_HEADING,
    monthly: Math.round(payment.monthly / 100),
    monthlyFormatted: payment.formatted,
    quoted: payment.quoted,
    lines,
    /*
     * A quoted payment carries one more disclaimer than an illustrated one,
     * and it is the important one: the rate on the estimate is the programme
     * we expect them to get, not the one the lender has written for them. The
     * figure is still an estimate until somebody underwrites it.
     */
    disclaimers: payment.quoted
      ? [
          "Estimated payment — your rate and term are set by the lender",
          "Subject to credit approval",
          "Soft credit pull only",
        ]
      : ["Subject to credit approval", "Soft credit pull only"],
  };
}

/**
 * Whether to show the block, and the QR code, on a given estimate.
 *
 * Both are three-state on the estimate: null means "whatever the company
 * setting says", true and false are this estimate deciding for itself. The
 * account-level default is what a rep changes once rather than on every job.
 *
 * **A commercial job answers null differently.** Jonathon, 2026-08-27: "when
 * job type = Commercial, the payment option defaults to off (not shown on the
 * presented estimate). Can be toggled on manually." This is consumer credit —
 * a soft pull on a person, ninety days to the first payment — and offering it
 * to a property manager buying a warehouse roof reads as though we have
 * mistaken who we are talking to. It is a default and nothing more: an
 * estimate that says true still shows it, on any kind of job.
 */
export function showFinancing(
  estimateSetting: boolean | null | undefined,
  companyDefault: boolean | null | undefined,
  jobType?: string | null,
  /**
   * `company_settings.financing_offered` — whether the company may offer
   * financing at all.
   *
   * Checked first and unconditionally. Jonathon, 2026-09-08: "let's hide
   * financing for now on all jobs until we get final approval from foundation
   * finance." That is a permission rather than a preference, so it has to beat
   * a rep's per-estimate setting; a switch a reader can defeat one estimate at
   * a time is not the switch he asked for.
   *
   * Undefined means "not loaded yet" and is treated as allowed, so a page
   * whose company settings have not arrived does not flicker the financing
   * block off and back on. The gate that matters is the one on the presented
   * document, which is built from settings that have loaded.
   */
  companyOffersFinancing?: boolean | null,
): boolean {
  if (companyOffersFinancing === false) return false;
  if (estimateSetting != null) return estimateSetting;
  if (isCommercialJob(jobType)) return false;
  return companyDefault ?? true;
}

export function showFinancingQr(
  estimateSetting: boolean | null | undefined,
  companyDefault: boolean | null | undefined,
): boolean {
  return estimateSetting ?? companyDefault ?? false;
}

/**
 * The terms a rep typed for this job.
 *
 * Every field is optional and every one of them may be null, because an
 * estimate saved before 2026-09-22 has none of them and reads back as a row of
 * nulls. Nothing here is required to price a roof — with no rate and no term
 * the page falls back to the $13-per-$1,000 illustration above, exactly as it
 * did before any of this existed.
 */
export interface FinancingTerms {
  /**
   * Cash down, subtracted from the total before the payment is worked out.
   *
   * Jonathon, 2026-09-22: "there needs to be a down payment option on
   * financing where we can put an amount and it subtracts it from the total."
   * It changes what is FINANCED and nothing else: the contract total, the
   * deposit and the four payment slots are untouched, because a down payment
   * is the customer's arrangement with the lender, not a line on our bill.
   */
  downPayment?: Cents | null | undefined;
  /** The length of the loan in months. 120 is ten years. */
  termMonths?: number | null;
  /** The annual rate as a percentage — 11.9 means 11.9% APR, not 0.119. */
  apr?: number | null;
}

/**
 * Why a rate and a term cannot be quoted as they stand, or null when they can.
 *
 * Jonathon, 2026-10-01: "Don't let a rep type a term with no rate and visa
 * versa" (ADR 0253). Both or neither: a term alone used to be quoted at the
 * opening plan's rate, a number nobody had given the customer. Pass the terms
 * the document will carry, with the company's default already filling an
 * empty box, because that is what the customer would be quoted.
 *
 * Refused at Present, not at every keystroke and not by the database: the
 * estimate card saves each box as the rep types it, so a constraint would
 * refuse the first of the two. Production had no estimate with only one of
 * the two on 2026-10-01.
 */
export function financingTermsGap(terms: {
  termMonths?: number | null | undefined;
  apr?: number | null | undefined;
}): string | null {
  const term = terms.termMonths != null;
  const rate = terms.apr != null;
  if (term && !rate) {
    return "A financing term needs a rate. Enter the rate, or clear the term.";
  }
  if (rate && !term) {
    return "A financing rate needs a term. Enter the term, or clear the rate.";
  }
  return null;
}

/** A term and a rate, either of which may be missing. */
export interface FinancingTermPair {
  termMonths?: number | null | undefined;
  apr?: number | null | undefined;
}

/**
 * A half-filled pair, and whose half it is.
 *
 * `estimate` when the rep typed a term or a rate on this estimate: the gap is
 * theirs and `financingTermsGap` says how to close it. `company` when the
 * estimate typed neither box and the company's default in Tools is the one
 * with a term and no rate, or a rate and no term (issue #644). Telling the rep
 * to "clear the term" then points at a box that is empty on the estimate (it
 * only shows the company's number as a placeholder), so the message names
 * Tools instead.
 */
export interface FinancingGap {
  source: "estimate" | "company";
  message: string;
}

export function financingTermsGapFor(
  estimate: FinancingTermPair | null | undefined,
  company: FinancingTermPair | null | undefined,
): FinancingGap | null {
  const typedTerm = estimate?.termMonths != null;
  const typedRate = estimate?.apr != null;
  const merged = {
    termMonths: typedTerm ? estimate?.termMonths : company?.termMonths,
    apr: typedRate ? estimate?.apr : company?.apr,
  };
  const gap = financingTermsGap(merged);
  if (gap == null) return null;
  if (typedTerm || typedRate) return { source: "estimate", message: gap };
  return {
    source: "company",
    message:
      merged.termMonths != null
        ? "The company's financing default in Tools has a term but no rate. Enter a rate on this estimate, or fix the default in Tools → Integrations."
        : "The company's financing default in Tools has a rate but no term. Enter a term on this estimate, or fix the default in Tools → Integrations.",
  };
}

/**
 * Why Present refuses these terms, or null when it does not.
 *
 * A pair the rep typed half of is refused whether or not the financing page
 * goes out, because the rep may still be typing (ADR 0253). A half-filled
 * company default is refused only when the financing page goes out: that is
 * the one way it reaches the customer, since a proposal with no financing page
 * carries no term, no rate and no monthly figure at all. Without this, one
 * half-filled box in Tools stopped every proposal, financing or not (#648).
 */
export function presentFinancingGap(
  estimate: FinancingTermPair | null | undefined,
  company: FinancingTermPair | null | undefined,
  financingPage: boolean,
): string | null {
  const gap = financingTermsGapFor(estimate, company);
  if (gap == null) return null;
  if (gap.source === "company" && !financingPage) return null;
  return gap.message;
}

/** The lender writes 1 to 20 years; anything outside that is a typo. */
export const FINANCING_MIN_TERM_MONTHS = 1;
export const FINANCING_MAX_TERM_MONTHS = 240;

/** What is left to finance: the total less whatever they put down. */
export function financedAmount(
  total: Cents,
  downPayment?: Cents | null,
): Cents {
  if (!Number.isFinite(total) || total <= 0) return 0 as Cents;
  const down = Number.isFinite(downPayment) ? Number(downPayment) : 0;
  if (down <= 0) return total;
  return Math.max(0, total - down) as Cents;
}

/**
 * The amortised monthly payment, in cents.
 *
 *     payment = P · r / (1 − (1 + r)^−n)
 *
 * the standard level-payment formula every lender quotes from, with r the
 * monthly rate and n the number of payments. Jonathon's own worked example —
 * $10,000 at 11.9% over 120 months — comes to $142.90, and the golden test
 * holds it to the cent.
 *
 * **Rounded up to the cent, never down.** The formula gives $142.8934 for that
 * example and $142.90 is the figure he quoted, which is what every lender
 * quotes: a payment rounded down is a payment that does not repay the loan, so
 * the schedule is written up. It also means the customer's copy can never
 * undercut the real payment by a cent, which is the only direction that error
 * matters in.
 *
 * Returns null rather than a number whenever the inputs could not produce an
 * honest one: nothing to finance, a rate that is not a rate, a term outside
 * what the lender writes. A zero rate is real (an interest-free promotion) and
 * divides evenly instead.
 */
export function amortizedPayment(
  principal: Cents,
  apr: number,
  termMonths: number,
): Cents | null {
  if (!Number.isFinite(principal) || principal <= 0) return null;
  if (!Number.isFinite(apr) || apr < 0 || apr >= 100) return null;
  if (!Number.isInteger(termMonths)) return null;
  if (
    termMonths < FINANCING_MIN_TERM_MONTHS ||
    termMonths > FINANCING_MAX_TERM_MONTHS
  ) {
    return null;
  }

  const monthlyRate = apr / 100 / 12;
  const payment =
    monthlyRate === 0
      ? principal / termMonths
      : (principal * monthlyRate) /
        (1 - Math.pow(1 + monthlyRate, -termMonths));

  if (!Number.isFinite(payment) || payment <= 0) return null;
  return Math.ceil(payment) as Cents;
}

/**
 * What the customer's copy says the payment is.
 *
 * Two kinds of answer, and which one this is matters enough to carry a flag.
 * A **quote** is what a rate and a term produce: the real amortised payment,
 * to the cent, because "$142.90" is a figure somebody can check against their
 * own loan and "approximately $143" is not. An **illustration** is the old
 * $13 per $1,000 — what the page has always shown, and what it still shows on
 * an estimate nobody has typed a rate on.
 *
 * Returns null when there is nothing to finance: a $0 estimate, or one paid
 * for entirely by the down payment. A customer paying cash is not shown a
 * monthly payment of anything.
 */
export interface FinancingPayment {
  /** The payment itself, in cents. */
  monthly: Cents;
  /** "$142.90" quoted, "$130" illustrated — the cents are the difference. */
  formatted: string;
  /** True when a rate and a term produced it. */
  quoted: boolean;
  /** The total less the down payment. */
  financed: Cents;
  downPayment: Cents;
  termMonths: number | null;
  apr: number | null;
}

export function financingPayment(
  total: Cents,
  terms?: FinancingTerms | null,
): FinancingPayment | null {
  if (!Number.isFinite(total) || total <= 0) return null;

  // A down payment larger than the job is a typo, not a refund: cap it at the
  // total so `financed` can never go negative and turn the payment upside down.
  const typed = Number.isFinite(terms?.downPayment)
    ? Number(terms?.downPayment)
    : 0;
  const downPayment = Math.min(Math.max(typed, 0), total) as Cents;
  const financed = financedAmount(total, downPayment);
  if (financed <= 0) return null;

  const apr = Number.isFinite(terms?.apr) ? Number(terms?.apr) : null;
  const termMonths = Number.isFinite(terms?.termMonths)
    ? Number(terms?.termMonths)
    : null;

  if (apr != null && termMonths != null) {
    const monthly = amortizedPayment(financed, apr, termMonths);
    if (monthly != null) {
      return {
        monthly,
        formatted: formatMoney(monthly),
        quoted: true,
        financed,
        downPayment,
        termMonths,
        apr,
      };
    }
  }

  // No usable terms — the illustration, on what is actually being financed.
  const dollars = financingMonthly(financed);
  if (dollars == null) return null;
  return {
    monthly: (dollars * 100) as Cents,
    formatted: `$${dollars}`,
    quoted: false,
    financed,
    downPayment,
    termMonths: null,
    apr: null,
  };
}

/**
 * The same question for an upgrade: what does adding this add a month?
 *
 * With terms typed it is the real payment on the upgrade's own price, at the
 * same rate over the same term — which is exactly the difference between two
 * loans, and so needs no down payment and no floor. Without them it is the
 * $13-per-$1,000 rounding `financingMonthlyAddition` has always done.
 */
export function financingAddition(
  amount: Cents,
  terms?: FinancingTerms | null,
): FinancingPayment | null {
  if (!Number.isFinite(amount) || amount <= 0) return null;

  const apr = Number.isFinite(terms?.apr) ? Number(terms?.apr) : null;
  const termMonths = Number.isFinite(terms?.termMonths)
    ? Number(terms?.termMonths)
    : null;

  if (apr != null && termMonths != null) {
    const monthly = amortizedPayment(amount, apr, termMonths);
    if (monthly != null) {
      return {
        monthly,
        formatted: formatMoney(monthly),
        quoted: true,
        financed: amount,
        downPayment: 0 as Cents,
        termMonths,
        apr,
      };
    }
  }

  const dollars = financingMonthlyAddition(amount);
  if (dollars == null) return null;
  return {
    monthly: (dollars * 100) as Cents,
    formatted: `$${dollars}`,
    quoted: false,
    financed: amount,
    downPayment: 0 as Cents,
    termMonths: null,
    apr: null,
  };
}

/** "11.9%" — a rate as the customer's copy prints it, without trailing zeros. */
export function formatApr(apr: number): string {
  if (!Number.isFinite(apr)) return "";
  const rounded = Math.round(apr * 100) / 100;
  return `${rounded}%`;
}

/** "120 months", and "10 years" when it divides evenly — both, as one line. */
export function formatTerm(termMonths: number): string {
  if (!Number.isFinite(termMonths) || termMonths <= 0) return "";
  const months = Math.round(termMonths);
  if (months % 12 === 0) {
    const years = months / 12;
    return `${months} months (${years} ${years === 1 ? "year" : "years"})`;
  }
  return `${months} months`;
}

/**
 * One lending programme the customer may pick between.
 *
 * Jonathon, 2026-09-22: "they can pick a 0% for 12 months with monthly
 * payments due, 9.9% for anywhere from 12-144 months … and 11.9% for 144
 * months", corrected the same day: "it makes more sense to do 2-12 years. 11.9
 * can also choose 2-12 years."
 *
 * A programme with `minMonths === maxMonths` has no term to choose and the
 * slider is not offered for it — 0% is twelve months or it is not that
 * programme. Both rate programmes are ranges, and the slider is for them.
 */
export interface FinancingProgram {
  /** Stable across a document, so a frozen copy can say which was chosen. */
  key: string;
  /** "9.9% APR" — the button the customer taps. */
  label: string;
  apr: number;
  minMonths: number;
  maxMonths: number;
  /** Where the slider starts. Jonathon: the 9.9% programme starts at 144. */
  defaultMonths: number;
  /** One line under the label, or absent. */
  note?: string;
}

/**
 * The programmes as Foundation Finance writes them, on 2026-09-22.
 *
 * The **fallback**, not the source: `company_settings.financing_programs`
 * holds them so a change of programme is a row edit rather than a deploy, the
 * same rule ADR 0033 set for the company's name and ADR 0201 for the rate. This
 * list is what a page uses when that column is empty or unreadable, because a
 * financing page with no programmes on it is worse than one quoting last
 * month's.
 *
 * Whole years on the sliders, two to twelve of them. Jonathon settled the range
 * himself on 2026-09-22 — "it makes more sense to do 2-12 years" — and it is
 * the same reason the stops are years rather than months: a customer who
 * landed on 90 months, or on a single year at 9.9%, would be shown a payment
 * nobody will write them.
 */
export const FINANCING_PROGRAMS: readonly FinancingProgram[] = [
  {
    key: "promo-0",
    label: "0% APR",
    apr: 0,
    minMonths: 12,
    maxMonths: 12,
    defaultMonths: 12,
    note: "12 months with monthly payments",
  },
  {
    key: "standard-9-9",
    label: "9.9% APR",
    apr: 9.9,
    minMonths: 24,
    maxMonths: 144,
    defaultMonths: 144,
    note: "Choose 2 to 12 years",
  },
  {
    key: "extended-11-9",
    label: "11.9% APR",
    apr: 11.9,
    minMonths: 24,
    maxMonths: 144,
    defaultMonths: 144,
    note: "Choose 2 to 12 years",
  },
];

/**
 * The smallest job a lender will write, in cents.
 *
 * Jonathon, 2026-09-22: "grand total estimate must be 2001 or greater." The
 * financing panel has said "$2K–$100K Loans" since ADR 0192; this is the same
 * floor, said as a number the calculator can check. Under it the calculator is
 * left off the page rather than quoting a loan nobody would write.
 */
export const FINANCING_MIN_TOTAL = 200_100 as Cents;
/** The largest loan advertised on the financing page. */
export const FINANCING_MAX_TOTAL = 10_000_000 as Cents;

/** Whether this job is big enough for any of it. */
export function financingApplies(total: Cents): boolean {
  return Number.isFinite(total) && total >= FINANCING_MIN_TOTAL;
}

/**
 * The months a programme actually offers, longest first.
 *
 * Whole years, clamped to the programme's own range, and always including both
 * ends — a programme of 12 to 144 offers twelve stops and a fixed one offers
 * the single month count it is written for.
 */
export function financingProgramTerms(program: FinancingProgram): number[] {
  const min = Math.max(1, Math.round(program.minMonths));
  const max = Math.max(min, Math.round(program.maxMonths));
  if (min === max) return [min];
  const months: number[] = [];
  for (let m = Math.ceil(min / 12) * 12; m <= max; m += 12) months.push(m);
  if (months[0] !== min) months.unshift(min);
  if (months[months.length - 1] !== max) months.push(max);
  return months;
}

/**
 * What a programme costs a month on this job.
 *
 * `downPayment` is whatever the rep put on the estimate — it comes off the
 * total here exactly as it does everywhere else, because the customer is
 * financing what is left, not the whole roof.
 *
 * Null when the job is too small to finance, when the down payment covers it,
 * or when the months asked for are not months this programme writes. A null is
 * a payment not shown, never a zero.
 */
export function financingProgramPayment(
  total: Cents,
  program: FinancingProgram,
  months: number,
  downPayment?: Cents | null,
): FinancingPayment | null {
  if (!financingApplies(total)) return null;
  // The lender's minimum applies to the loan, not just the contract price.
  // A large down payment can leave too little to finance.
  const principal = financedAmount(total, downPayment);
  if (principal < FINANCING_MIN_TOTAL || principal > FINANCING_MAX_TOTAL)
    return null;
  if (!financingProgramTerms(program).includes(months)) return null;
  return financingPayment(total, {
    downPayment,
    termMonths: months,
    apr: program.apr,
  });
}

/**
 * The programmes as a stored row hands them over, or the built-in list.
 *
 * `company_settings.financing_programs` is jsonb written by a migration and
 * edited by hand, so it is read defensively: anything that is not a usable
 * programme is dropped, and a row that leaves nothing usable falls back to
 * `FINANCING_PROGRAMS` entirely. A financing page with no programmes on it is
 * a worse failure than one quoting the list we shipped with.
 */
export function financingPrograms(raw: unknown): readonly FinancingProgram[] {
  if (!Array.isArray(raw)) return FINANCING_PROGRAMS;
  const parsed: FinancingProgram[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const row = entry as Record<string, unknown>;
    const key = typeof row.key === "string" ? row.key.trim() : "";
    const label = typeof row.label === "string" ? row.label.trim() : "";
    // Number(null) and Number("") are both zero; neither is an approved 0%
    // programme. Only an explicit numeric rate may reach a customer.
    const apr =
      typeof row.apr === "number" ||
      (typeof row.apr === "string" && row.apr.trim() !== "")
        ? Number(row.apr)
        : Number.NaN;
    const minMonths = Number(row.minMonths);
    const maxMonths = Number(row.maxMonths);
    const defaultMonths = Number(row.defaultMonths ?? maxMonths);
    if (!key || !label) continue;
    if (!Number.isFinite(apr) || apr < 0 || apr >= 100) continue;
    if (!Number.isInteger(minMonths) || !Number.isInteger(maxMonths)) continue;
    if (minMonths < FINANCING_MIN_TERM_MONTHS) continue;
    if (maxMonths > FINANCING_MAX_TERM_MONTHS || maxMonths < minMonths)
      continue;
    const note = typeof row.note === "string" ? row.note.trim() : "";
    const program: FinancingProgram = {
      key,
      label,
      apr,
      minMonths,
      maxMonths,
      // The slider only offers the terms returned by financingProgramTerms.
      // A default between the endpoints but between stops would open with no
      // payment, so use the longest offered term instead.
      defaultMonths: maxMonths,
      ...(note ? { note } : {}),
    };
    if (financingProgramTerms(program).includes(defaultMonths)) {
      program.defaultMonths = defaultMonths;
    }
    parsed.push(program);
  }
  return parsed.length > 0 ? parsed : FINANCING_PROGRAMS;
}

/**
 * Which programme a page starts on.
 *
 * The rep's own rate when it matches one — a proposal that prints "$142.90/mo"
 * beside the price and then opens its calculator on a different programme is
 * quoting the same roof two ways. Otherwise the longest, cheapest monthly
 * offer, which is the one that answers "I cannot afford that" and is why the
 * 9.9% programme defaults to 144 months.
 */
export function defaultFinancingProgram(
  programs: readonly FinancingProgram[],
  apr?: number | null,
): FinancingProgram | null {
  if (programs.length === 0) return null;
  if (apr != null && Number.isFinite(apr)) {
    const matched = programs.find((program) => program.apr === apr);
    if (matched) return matched;
  }
  return programs.reduce((best, program) =>
    program.maxMonths > best.maxMonths ||
    (program.maxMonths === best.maxMonths && program.apr < best.apr)
      ? program
      : best,
  );
}
